/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles } from '@material-ui/core';
import Button from 'components/Button';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import Permission from 'components/ValidPermission';
import AdvisorButtons from 'containers/Advisors/ListAdvisors/AdvisorButtons';
import AdvisorDetails from 'containers/Advisors/ListAdvisors/AdvisorDetails';
import AdvisorImpact from 'containers/Advisors/ListAdvisors/AdvisorImpact';
import { format } from 'date-fns';
import { Formik } from 'formik';
import { Query, useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import {
  revertAdvisorRequest,
  applyAdvisorRequest,
  ignoreAdvisorRequest,
} from 'modules/advisors/actions';
import {
  AdvisorProps,
  ListAdvisorsDataProps,
} from 'modules/advisors/interfaces';
import { PermissionsData } from 'modules/authenticate/interfaces';
import { getEnvironmentAdvisorsRequest } from 'modules/environments/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import { formatCurrency } from 'utils/helpers';
import IgnoreMessage from 'containers/Advisors/ListAdvisors/AdvisorButtons/ignoreMessage';
import TitleBehavior from '../../FormBehavior/TitleBehavior';
import screenFields from './screenFields';
import styles from './styles';

const useStyles = makeStyles(styles);

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'list_scaling_advisor_details',
  'can_revert_scaling_advisor',
];

interface AdvisorHistoryProps {
  isLoading: boolean;
  environmentAdvisorsList: ListAdvisorsDataProps;
  permissions?: PermissionsData;
  applyAdvisorRequest: (props) => void;
  getEnvironmentAdvisorsRequest: (props: {
    environmentUuid: string;
    query: Query;
  }) => void;
  ignoreAdvisorRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  revertAdvisorRequest: (props) => void;
}

const AdvisorHistory = ({
  isLoading,
  environmentAdvisorsList,
  permissions,
  applyAdvisorRequest,
  getEnvironmentAdvisorsRequest,
  ignoreAdvisorRequest,
  modalOpen,
  modalClose,
  revertAdvisorRequest,
}: AdvisorHistoryProps) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();

  const {
    query,
    itemOrder,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    setQuery,
  } = useTableFilters({
    offset: 0,
    limit: 25,
    search: '',
    status: 'applied',
  });

  const [refresh, setRefresh] = React.useState(false);

  const headers: TableCellHeaderProps[] = [
    {
      key: 'advisor_type',
      i18n: 'TYPE_KEY',
      order: true,
    },
    {
      key: 'created_at',
      i18n: 'CREATED_AT_KEY',
      style: { width: '130px' },
      order: true,
    },
    {
      key: 'estimated_monthly_cost_savings',
      i18n: 'ADVISOR_PAGE.ESTIMATED_COST_SAVINGS_KEY',
      order: true,
    },
    {
      key: 'impact',
      i18n: 'RISK_KEY',
      order: true,
    },
    {
      key: 'status',
      i18n: 'STATUS_KEY',
      order: true,
    },
    {
      key: 'actions',
      i18n: 'ACTIONS_KEY',
      centerTitle: true,
      hidden:
        !!permissions &&
        requiredActionsPermissions.every(
          permission => !permissions[permission]
        ),
    },
  ].filter(header => !header.hidden);

  const getAdvisors = React.useCallback(
    (refresh = false) => {
      getEnvironmentAdvisorsRequest({ environmentUuid: id, query });
      setRefresh(refresh);
    },
    [getEnvironmentAdvisorsRequest, id, query]
  );

  React.useEffect(() => {
    getAdvisors();
  }, [getAdvisors]);

  const handleGetAdvisor = (item: AdvisorProps) => {
    modalOpen({
      overrideClasses: {
        content: {
          padding: 12,
        },
      },
      title: i18n.t('ADVISOR_PAGE.OPTIMIZATION_DETAILS_KEY'),
      maxWidth: 'md',
      footer: (
        <AdvisorButtons
          onClose={modalClose}
          isLoading={isLoading}
          advisorUuid={item?.advisor_uuid}
          getAdvisors={getAdvisors}
        />
      ),
      content: (
        <AdvisorDetails
          getTranslatedImpact={impact => <AdvisorImpact impact={impact} />}
          advisorUuid={item?.advisor_uuid}
        />
      ),
    });
  };

  const handleApply = (item: AdvisorProps) => {
    modalOpen({
      title: i18n.t('ADVISOR_PAGE.WANT_APPLY_ADVISOR_KEY'),
      content: i18n.t('ADVISOR_PAGE.CONFIRM_APPLY_ADVISOR_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="confirm-button"
            onClick={() => {
              applyAdvisorRequest({
                advisorUuid: item?.advisor_uuid,
                getAdvisorsFn: () => getAdvisors(),
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleIgnore = (item: AdvisorProps) => {
    modalOpen({
      title: i18n.t('ADVISOR_PAGE.WANT_IGNORE_ADVISOR_KEY'),
      content: (
        <IgnoreMessage
          onClose={() => modalClose()}
          onConfirm={ignored_reason => {
            ignoreAdvisorRequest({
              advisorUuid: item?.advisor_uuid,
              ignored_reason,
              getAdvisorsFn: () => getAdvisors(),
            });
            modalClose();
          }}
        />
      ),
      noBorder: true,
    });
  };

  const handleRevert = (item: AdvisorProps) => {
    modalOpen({
      title: i18n.t('ADVISOR_PAGE.WANT_REVERT_ADVISOR_KEY'),
      content: i18n.t('ADVISOR_PAGE.CONFIRM_REVERT_ADVISOR_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="confirm-button"
            onClick={() => {
              revertAdvisorRequest({
                advisorUuid: item?.advisor_uuid,
                getAdvisorsFn: () => getAdvisors(),
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const getMenuActionItems = (item: AdvisorProps) =>
    [
      {
        key: 'btn-details-advisor',
        label: i18n.t('DETAILS_KEY'),
        onClick: () => handleGetAdvisor(item),
        hidden: !permissions?.list_scaling_advisor_details,
      },
      {
        key: 'btn-apply-advisor',
        label: i18n.t('APPLY_KEY'),
        onClick: () => handleApply(item),
        hidden:
          item?.status !== 'pending' || !permissions?.can_apply_scaling_advisor,
      },
      {
        key: 'btn-ignore-advisor',
        label: i18n.t('IGNORE_KEY'),
        onClick: () => handleIgnore(item),
        hidden:
          item?.status !== 'pending' ||
          !permissions?.can_ignore_scaling_advisor,
      },
      {
        key: 'btn-revert-advisor',
        label: i18n.t('REVERT_KEY'),
        onClick: () => handleRevert(item),
        hidden:
          item?.status !== 'applied' ||
          !permissions?.can_revert_scaling_advisor,
      },
    ].filter(action => !action?.hidden);

  return (
    <Grid item xs={12}>
      <TitleBehavior title={i18n.t('ADVISOR_PAGE.LIST_ADVISORS_TITLE_KEY')} />

      <Formik
        initialValues={{
          advisor_type: query.advisor_type || 'all',
          status: query.status || 'all',
        }}
        onSubmit={values => setQuery(prev => ({ ...prev, ...values }))}
        enableReinitialize
      >
        {({ setFieldValue, errors, values }) => (
          <Grid
            container
            xs={12}
            justify="space-between"
            style={{ marginTop: 32, marginBottom: 16, alignItems: 'center' }}
          >
            <Grid container xs={10} spacing={2}>
              <GenericForm
                fields={values}
                errors={errors}
                screenFields={screenFields}
                setFieldValue={(key, value) => {
                  setFieldValue(key, value);
                  setQuery(prev => ({
                    ...prev,
                    offset: 0,
                    [key]: value === 'all' ? '' : value,
                  }));
                }}
                overrideClasses={{
                  selectLabel: { fontSize: 12, fontWeight: 500 },
                }}
              />
            </Grid>

            <RefreshButton
              id="btn-refresh"
              onClick={() => getAdvisors(true)}
              animate={isLoading}
              className={classes.refreshButton}
              disabled={isLoading}
            />
          </Grid>
        )}
      </Formik>

      <TableItems
        header={headers.map(item => (
          <TableCell
            key={item?.key}
            style={item?.style}
            centerTitle={item?.centerTitle}
            markOderItem={itemOrder === item?.key}
            order={item?.order}
            handleOrder={() => (item?.order ? handleOrder(item?.key) : null)}
          >
            {i18n.t(item?.i18n)}
          </TableCell>
        ))}
        renderItems={(item: AdvisorProps) => (
          <TableRow key={item?.advisor_uuid}>
            <TableCell>
              {i18n.t(`${item?.advisor_type?.toUpperCase()}_KEY`)}
            </TableCell>
            <TableCell>
              {format(new Date(item?.created_at), 'dd/MM/yyyy')}
            </TableCell>
            <TableCell>
              {!!item?.estimated_monthly_cost_savings &&
                formatCurrency(item?.estimated_monthly_cost_savings)}
            </TableCell>
            <TableCell>
              <AdvisorImpact impact={item?.impact} />
            </TableCell>
            <TableCell>
              {i18n.t(`${item?.status?.toUpperCase()}_KEY`)}
            </TableCell>
            <Permission value={requiredActionsPermissions}>
              <TableCell textAlignCenter>
                <MenuActions id={`btn-options-${item?.advisor_uuid}`}>
                  {getMenuActionItems(item)?.length > 0 ? (
                    getMenuActionItems(item)?.map(action => (
                      <MenuActionItem
                        key={`${action?.key}-${item?.advisor_uuid}`}
                        id={action?.key}
                        onClick={action?.onClick}
                      >
                        {action?.label}
                      </MenuActionItem>
                    ))
                  ) : (
                    <MenuActionItem>
                      {i18n.t('NO_ACTIONS_AVAILABLE_KEY')}
                    </MenuActionItem>
                  )}
                </MenuActions>
              </TableCell>
            </Permission>
          </TableRow>
        )}
        data={environmentAdvisorsList}
        isLoading={!refresh && isLoading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        notFoundText={i18n.t('NO_ADVISOR_KEY')}
      />
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { isLoadingEnvironmentAdvisorsList, environmentAdvisorsList },
}: ApplicationState) => ({
  isLoading: isLoadingEnvironmentAdvisorsList,
  environmentAdvisorsList,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  applyAdvisorRequest,
  getEnvironmentAdvisorsRequest,
  ignoreAdvisorRequest,
  modalOpen,
  modalClose,
  revertAdvisorRequest,
})(AdvisorHistory);
